import {
  Card,
  Checkbox,
  Col,
  DatePicker,
  Flex,
  Form,
  Input,
  Row,
  Space,
  Tag
} from 'antd'
import { FormInstance } from 'antd/lib'
import dayjs from 'dayjs'

import {
  ClassifierCategory,
  ClassifierType,
  InquiryDocumentResponse,
  InventoryDocumentState,
  InventoryDocumentStockItemResponse,
  IssuanceDocumentResponse
} from 'src/api/requests'
import { requiredRules } from 'src/app-constants'
import { ClassifierInfiniteSelect, Text } from 'src/components'
import { CounterPartSelect } from 'src/features/counterParts/components'
import { getStatus } from 'src/features/stock/utils'
import { useClassifierId } from 'src/hooks'
import {
  dayMonthYearBackSlash,
  dayMonthYearHourMinutesFormatSeparator
} from 'src/libs'
import { useLocales } from 'src/locales'

type Props = {
  data?: IssuanceDocumentResponse
  inquireData?: InquiryDocumentResponse
  form?: FormInstance
}

export const IssuanceHeader = ({ data, inquireData, form }: Props) => {
  const { t } = useLocales()
  const showTransportInfo = Form.useWatch('showTransportInfo', form)
  const inquiryItems = Form.useWatch('inquiryItems', form)

  const updateReasonId = useClassifierId(
    ClassifierType.INQUIRY_UPDATE_REASON,
    ClassifierCategory.STOCKS
  )

  const isRejected = data?.state === InventoryDocumentState.REJECTED
  const isSubmitted = !!data?.relatedDocuments?.length
  const hasInquireState =
    inquireData && inquireData?.state !== InventoryDocumentState.NEW
  const hasQuantityChanged = inquiryItems?.some(
    (item: InventoryDocumentStockItemResponse, index: number) =>
      item?.quantity !== data?.items?.[index].quantity
  )
  const hasUpdatedReason = !!inquireData?.updateReason

  return (
    <Flex vertical gap={16}>
      <Card>
        <Flex vertical gap={16}>
          <Flex vertical gap={8}>
            <Flex justify="space-between">
              <Space>
                <Text weight={400}>
                  {t('stock_applications.issuance_based_on_inquire')}:
                </Text>
                <Text strong>#{data?.code}</Text>
              </Space>
              <Tag
                color={
                  getStatus(
                    t,
                    (!hasInquireState ? data?.state : inquireData?.state) ??
                      InventoryDocumentState.NEW
                  ).color
                }
              >
                {
                  getStatus(
                    t,
                    (!hasInquireState ? data?.state : inquireData?.state) ??
                      InventoryDocumentState.NEW
                  ).text
                }
              </Tag>
            </Flex>

            <Space>
              <Text weight={400}>{t('stock_applications.request_date')}:</Text>
              <Text strong>
                {dayjs
                  .utc(data?.documentDate)
                  .format(dayMonthYearHourMinutesFormatSeparator)}
              </Text>
            </Space>

            {data?.processedOn && (
              <Space>
                <Text weight={400}>
                  {isRejected
                    ? `${t('stock_applications.refusal_date')}:`
                    : `${t('stock_applications.accepted_date')}:`}
                </Text>
                <Text strong>
                  {dayjs
                    .utc(data?.processedOn)
                    .tz(dayjs.tz.guess())
                    .format(dayMonthYearHourMinutesFormatSeparator)}
                </Text>
              </Space>
            )}

            <Space>
              <Text weight={400}>{t('stock_applications.initiated_by')}:</Text>
              <Text strong>
                {data?.initializedBy?.firstName} {data?.initializedBy?.lastName}{' '}
                -
              </Text>
              <Text strong>{data?.beneficiary?.name}</Text>
            </Space>

            {data?.processedBy && (
              <Space>
                <Text weight={400}>
                  {isRejected
                    ? `${t('stock_applications.refused_by')}:`
                    : `${t('stock_applications.accepted_by')}:`}
                </Text>
                <Text strong>
                  {data?.processedBy?.firstName} {data?.processedBy?.lastName} -
                </Text>
                <Text strong>{data?.beneficiary?.name}</Text>
              </Space>
            )}

            <Space>
              <Text weight={400}>{t('stock_applications.requested_to')}:</Text>
              <Text strong>{data?.supplier?.name}</Text>
            </Space>
          </Flex>
        </Flex>
      </Card>

      <Card>
        <Flex vertical gap={24}>
          <Form.Item shouldUpdate>
            {({ getFieldValue }) => {
              const isDisabled =
                !getFieldValue('invoiceDate') || !getFieldValue('contractId')

              return (
                <Row gutter={[24, 16]}>
                  <Col span={12}>
                    <Form.Item
                      name="documentDate"
                      label={t(`stock.invoice_date`)}
                      rules={requiredRules}
                    >
                      <DatePicker
                        className="full-width"
                        disabled={isDisabled}
                        format={dayMonthYearBackSlash}
                      />
                    </Form.Item>
                  </Col>

                  <Col span={12}>
                    <Form.Item
                      name="documentNumber"
                      label={t(`stock.accompanying_invoice`)}
                      rules={requiredRules}
                    >
                      <Input
                        className="full-width"
                        disabled={isDisabled}
                        placeholder={t(`stock.accompanying_invoice`)}
                      />
                    </Form.Item>
                  </Col>

                  <Col span={12}>
                    <Form.Item
                      name={['supplier', 'name']}
                      label={t(`stock.provider`)}
                      rules={requiredRules}
                    >
                      <Input className="full-width" disabled />
                    </Form.Item>
                  </Col>

                  <Col span={12}>
                    <Form.Item
                      name={['beneficiary', 'name']}
                      label={t(`counterparts.purchase_center`)}
                      rules={requiredRules}
                    >
                      <Input
                        className="full-width"
                        disabled
                        placeholder={t(`counterparts.purchase_center`)}
                      />
                    </Form.Item>
                  </Col>

                  {(hasUpdatedReason || hasQuantityChanged) && (
                    <Col span={12}>
                      <Form.Item
                        name="updateReasonId"
                        label={t('stock.edit_reason')}
                        rules={requiredRules}
                      >
                        <ClassifierInfiniteSelect
                          id={updateReasonId}
                          placeholder={t('stock.edit_reason')}
                        />
                      </Form.Item>
                    </Col>
                  )}

                  {inquireData &&
                    (!!data?.transporter?.id || !isRejected || isSubmitted) && (
                      <Col span={12}>
                        <Form.Item
                          name="takeoverDate"
                          label={t(`stock_administration.pickup_date`)}
                          rules={requiredRules}
                        >
                          <DatePicker
                            className="full-width"
                            format={dayMonthYearBackSlash}
                          />
                        </Form.Item>
                      </Col>
                    )}
                </Row>
              )
            }}
          </Form.Item>

          {inquireData && (
            <Card>
              <Flex vertical gap={16}>
                <Col span={24}>
                  <Form.Item name="showTransportInfo" valuePropName="checked">
                    <Checkbox>
                      {t('stock_administration.register_transport')}{' '}
                    </Checkbox>
                  </Form.Item>
                </Col>

                {showTransportInfo && (
                  <Col span={24}>
                    <Row gutter={24}>
                      <Col span={12}>
                        <Form.Item
                          name="transporterId"
                          label={t(`stock_administration.select_transport`)}
                          rules={requiredRules}
                        >
                          <CounterPartSelect
                            allowClear={false}
                            placeholder={t(
                              `stock_administration.select_transport`
                            )}
                          />
                        </Form.Item>
                      </Col>

                      <Col span={12}>
                        <Form.Item
                          name="accompanyingNotice"
                          label={t(`stock_administration.way_bill`)}
                          rules={requiredRules}
                        >
                          <Input
                            className="full-width"
                            placeholder={t(`stock_administration.way_bill`)}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Col>
                )}
              </Flex>
            </Card>
          )}
        </Flex>
      </Card>
    </Flex>
  )
}
